import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useSearchParams } from 'react-router-dom';

import { useContentSearch } from './use-content-search';
import { searchTargetHref } from './search-navigation';
import type { SearchContainerResult } from './types';
import { activeSearchOpenIdFromPath } from '@/lib/layout/sidebar-tab';

type SearchNavContextValue = {
  query: string;
  setQuery: (query: string) => void;
  grouped: SearchContainerResult[];
  loading: boolean;
  error: string | null;
  activeOpenId: string | null;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  openTarget: (target: SearchContainerResult) => void;
  selectByIndex: (index: number) => void;
};

const SearchNavContext = createContext<SearchNavContextValue | null>(null);

export function SearchNavProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';
  const [query, setQueryState] = useState(urlQuery);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSearchTab = pathname.startsWith('/search');

  useEffect(() => {
    if (onSearchTab) {
      setQueryState(urlQuery);
    }
  }, [onSearchTab, urlQuery]);

  const setQuery = useCallback(
    (next: string) => {
      setQueryState(next);
      if (!onSearchTab) {
        return;
      }
      const params = new URLSearchParams(searchParams);
      const trimmed = next.trim();
      if (trimmed) {
        params.set('q', trimmed);
      } else {
        params.delete('q');
      }
      setSearchParams(params, { replace: true });
    },
    [onSearchTab, searchParams, setSearchParams],
  );

  const { grouped, loading, error } = useContentSearch(onSearchTab ? query : '', { limit: 30 });

  useEffect(() => {
    setSelectedIndex(0);
  }, [grouped.length, query]);

  const activeOpenId = activeSearchOpenIdFromPath(pathname);

  const openTarget = useCallback(
    (target: SearchContainerResult) => {
      const href = searchTargetHref(target, { query, blockId: target.topBlockId });
      router.push(href);
    },
    [query, router],
  );

  const selectByIndex = useCallback(
    (index: number) => {
      if (grouped.length === 0) {
        return;
      }
      const safe = ((index % grouped.length) + grouped.length) % grouped.length;
      setSelectedIndex(safe);
      openTarget(grouped[safe]!);
    },
    [grouped, openTarget],
  );

  const value = useMemo(
    () => ({
      query,
      setQuery,
      grouped,
      loading,
      error,
      activeOpenId,
      selectedIndex,
      setSelectedIndex,
      openTarget,
      selectByIndex,
    }),
    [
      query,
      setQuery,
      grouped,
      loading,
      error,
      activeOpenId,
      selectedIndex,
      openTarget,
      selectByIndex,
    ],
  );

  return <SearchNavContext.Provider value={value}>{children}</SearchNavContext.Provider>;
}

export function useSearchNav(): SearchNavContextValue {
  const ctx = useContext(SearchNavContext);
  if (!ctx) {
    throw new Error('useSearchNav must be used within SearchNavProvider');
  }
  return ctx;
}

export function useSearchNavOptional(): SearchNavContextValue | null {
  return useContext(SearchNavContext);
}
